/**
 * Meo Attachment Utilities
 *
 * Handles client-side processing of files attached to Meo AI messages:
 * - Image resize (max 1024px JPEG 80%)
 * - DOCX text extraction via mammoth.js
 * - Size validation against backend AI_CONFIG limits
 * - Thumbnail generation for image previews
 * - File-to-base64 conversion
 */

import { MeoAttachment } from '@app/_interfaces/meo.interface';
import { log } from '@app/_services/logger';

// Limits matching functions/src/functions/ai/ai_config.ts
const MAX_ATTACHMENT_SIZE_BYTES = 10_000_000; // 10MB for images/PDFs
const MAX_IMAGE_DIMENSION = 1024;
const JPEG_QUALITY = 0.8;
const THUMBNAIL_SIZE = 80;

/** Accepted MIME types for Meo attachments. */
const ACCEPTED_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
]);

/** Human-readable accept string for file inputs. */
export const MEO_ACCEPTED_FILE_TYPES = 'image/jpeg,image/png,image/gif,image/webp,application/pdf,.docx';

/**
 * Validate and process a file for Meo attachment.
 * Returns null with a reason if the file is invalid.
 */
export async function processFileForMeo(file: File): Promise<{ attachment: MeoAttachment | null; error: string | null }> {
  // Validate MIME type
  const mimeType = file.type || guessMimeType(file.name);
  if (!ACCEPTED_MIME_TYPES.has(mimeType)) {
    return { attachment: null, error: 'meo_error_unsupported_file' };
  }

  // Validate size
  if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
    return { attachment: null, error: 'meo_error_file_too_large' };
  }

  try {
    if (mimeType.startsWith('image/')) {
      return { attachment: await processImage(file), error: null };
    }

    if (mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      return { attachment: await processDocx(file), error: null };
    }

    // PDF — send as-is (base64)
    return { attachment: await processGenericFile(file, mimeType), error: null };
  } catch (err) {
    log.error('processFileForMeo', 'File processing failed', err as Error);
    return { attachment: null, error: 'meo_error_file_processing' };
  }
}

/**
 * Process an image: resize to max 1024px, convert to JPEG 80%, generate thumbnail.
 */
async function processImage(file: File): Promise<MeoAttachment> {
  const bitmap = await createImageBitmap(file);
  const { width, height } = bitmap;

  // Determine if resize is needed
  const needsResize = width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION;
  let targetWidth = width;
  let targetHeight = height;

  if (needsResize) {
    const ratio = Math.min(MAX_IMAGE_DIMENSION / width, MAX_IMAGE_DIMENSION / height);
    targetWidth = Math.round(width * ratio);
    targetHeight = Math.round(height * ratio);
  }

  // Resize and compress to JPEG
  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
  bitmap.close();

  const base64 = canvas.toDataURL('image/jpeg', JPEG_QUALITY).split(',')[1];

  // Generate thumbnail
  const thumbCanvas = document.createElement('canvas');
  const thumbRatio = Math.min(THUMBNAIL_SIZE / targetWidth, THUMBNAIL_SIZE / targetHeight, 1);
  thumbCanvas.width = Math.round(targetWidth * thumbRatio);
  thumbCanvas.height = Math.round(targetHeight * thumbRatio);
  const thumbCtx = thumbCanvas.getContext('2d')!;
  thumbCtx.drawImage(canvas, 0, 0, thumbCanvas.width, thumbCanvas.height);
  const thumbnailDataUrl = thumbCanvas.toDataURL('image/jpeg', 0.6);

  log.info('processImage', 'Image processed', {
    original: `${width}x${height}`,
    resized: `${targetWidth}x${targetHeight}`,
    originalSize: file.size,
    base64Length: base64.length,
  });

  return {
    fileName: file.name,
    mimeType: 'image/jpeg',
    base64,
    originalSize: file.size,
    thumbnailDataUrl,
  };
}

/**
 * Process a DOCX file: extract text via mammoth.js.
 * The extracted text is sent as context alongside the base64 file.
 */
async function processDocx(file: File): Promise<MeoAttachment> {
  const arrayBuffer = await file.arrayBuffer();
  const base64 = arrayBufferToBase64(arrayBuffer);

  let extractedText = '';
  try {
    const mammoth = await import('mammoth');
    const result = await mammoth.extractRawText({ arrayBuffer });
    extractedText = result.value.trim();
    log.info('processDocx', 'Text extracted', { charCount: extractedText.length });
  } catch (err) {
    log.warn('processDocx', 'mammoth extraction failed, sending file as-is', err as Error);
  }

  return {
    fileName: file.name,
    mimeType: file.type,
    base64,
    originalSize: file.size,
    extractedText: extractedText || undefined,
  };
}

/**
 * Process a generic file (PDF): convert to base64.
 */
async function processGenericFile(file: File, mimeType: string): Promise<MeoAttachment> {
  const arrayBuffer = await file.arrayBuffer();
  const base64 = arrayBufferToBase64(arrayBuffer);

  return {
    fileName: file.name,
    mimeType,
    base64,
    originalSize: file.size,
  };
}

/** Convert ArrayBuffer to base64 string. Uses chunked approach to avoid stack overflow on large files. */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 8192;
  const chunks: string[] = [];
  for (let i = 0; i < bytes.byteLength; i += chunkSize) {
    const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.byteLength));
    chunks.push(String.fromCharCode(...chunk));
  }
  return btoa(chunks.join(''));
}

/** Guess MIME type from file extension when browser doesn't provide one. */
function guessMimeType(fileName: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'gif':
      return 'image/gif';
    case 'webp':
      return 'image/webp';
    case 'pdf':
      return 'application/pdf';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    default:
      return 'application/octet-stream';
  }
}
